import {
  ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostBinding, Input, OnInit,
  Output, QueryList, Renderer2, ViewChildren, ViewEncapsulation
} from '@angular/core';

import { SwiperOptions } from 'swiper/types';

import { IExtendedLink, ILinkMedia, ISliderInfo, PositionEnum, SdThemeComponent, SizesEnum } from '@seb/web-design-system/core';
import { ArgumentsLayoutEnum, IArguments, IArgumentWithConfig, SdArgumentsComponent } from '@seb/web-design-system/arguments';
import { SdSliderComponent } from '@seb/web-design-system/sliders';

@Component({
  selector: 'sd-arguments-slider',
  templateUrl: './arguments-slider.component.html',
  styleUrls: ['./arguments-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    SdSliderComponent,
    SdArgumentsComponent
  ]
})
export class SdArgumentsSliderComponent extends SdThemeComponent implements OnInit {
  @Input()
  isLoading = false;

  @Input()
  context = 'Arguments';

  @Input()
  data: IArgumentWithConfig[];

  @Input()
  layout: ArgumentsLayoutEnum = ArgumentsLayoutEnum.default;

  @Input()
  twicPicsAutoSize = true;

  @ViewChildren('arguments')
  arguments: QueryList<ElementRef>;

  @Output()
  videoPlayed = new EventEmitter<{ status: string; title: string }>();

  @Output()
  videoEnded = new EventEmitter<{ status: string; title: string }>();

  @Output()
  slideNext = new EventEmitter<ISliderInfo>();

  @Output()
  slidePrev = new EventEmitter<ISliderInfo>();

  @Output()
  clicked = new EventEmitter<IExtendedLink | ILinkMedia>();

  @Input()
  class = ''; // override the standard class attr with a new one.
  @HostBinding('class')
  get hostClasses(): string {
    return [this.layout ? 'is-' + this.layout : '', this.class]
      .filter(Boolean)
      .join(' ');
  }

  sizesEnum = SizesEnum;
  argumentsSliderConfig: SwiperOptions = {
    pagination: {
      dynamicBullets: false,
      type: 'bullets',
      clickable: true
    },
    loop: false,
    watchOverflow: true,
    breakpoints: {
      0: {
        spaceBetween: 0,
        slidesPerView: 1,
        slidesPerGroup: 1
      },
      768: {
        spaceBetween: 0,
        slidesPerView: 1,
        slidesPerGroup: 1
      },
      1280: {
        spaceBetween: 0,
        slidesPerView: 1,
        slidesPerGroup: 1
      }
    }
  };

  constructor(private renderer: Renderer2) {
    super();
  }

  sliderInitCallback() {
    if (this.arguments) {
      const bodySelector = this.arguments.toArray();
      if (bodySelector) {
        bodySelector.forEach((el) => {
          this.renderer.removeAttribute(el.nativeElement, 'style');
        });
        this.setHeight(bodySelector);
      }
    }
  }

  getMaxHeight(bodySelector) {
    let maxHeight = 0;
    bodySelector.forEach((el) => {
      const thisH = el.nativeElement.clientHeight;
      if (thisH > maxHeight) {
        maxHeight = thisH;
      }
    });
    return maxHeight;
  }

  setHeight(bodySelector) {
    const maxHeight = this.getMaxHeight(bodySelector);
    bodySelector.forEach((el) => {
      this.renderer.setStyle(el.nativeElement, 'height', maxHeight + 'px');
    });
  }

  ngOnInit() {
    this.updateClass(0);
  }

  onSlideNext(event: ISliderInfo) {
    this.slideNext.emit(event);
    this.updateClass(event.index);
  }

  onSlidePrev(event: ISliderInfo) {
    this.slidePrev.emit(event);
    this.updateClass(event.index);
  }

  onVideoPlayed(item: IArguments) {
    this.videoPlayed.emit({
      status: 'play',
      title: item?.content?.heading?.data?.title
    });
  }

  onVideoEnded(item: IArguments) {
    this.videoEnded.emit({
      status: 'ended',
      title: item?.content?.heading?.data?.title
    });
  }

  onClicked(event: IExtendedLink | ILinkMedia) {
    this.clicked.emit(event);
  }

  updateClass(index) {
    this.class = this.data[index]?.layoutTextConfig
      ? 'is-layout-' + this.data[index]?.layoutTextConfig
      : 'is-layout-' + PositionEnum.left;
    this.class += this.data[index]?.isSmall ? ' is-small' : '';
  }
}
